import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { PaymentSource } from '../../../../../constants';
import { NULL_USER } from '../../../../../lib/user';
import { hrefFor } from '../../../../../routers';
import { ApplePayOnValidateMerchant } from '../../../../ApplePay';
import { ApplePaymentSummary } from '../../../../../hooks/useCart';
import { useUser } from '../../../../../hooks/useUser';
import { usePlaceOrderMutation } from '../../../../../hooks/useCheckout/usePlaceOrderMutation';

interface SessionProps {
  session: ApplePaySession;
  cartId?: string;
  request?: ApplePayJS.ApplePayPaymentRequest;
  status?: string;
}

const SessionPaymentAuthorized: React.FC<SessionProps> = ({ session, cartId, request, status }) => {
  const {
    setUserData,
    user: { isLoggedIn, firstName }
  } = useUser();
  const history = useHistory();

  const [placeOrder] = usePlaceOrderMutation();

  useEffect(() => {
    session.onpaymentauthorized = async event => {
      if (!request) return;

      const shippingAddress = {
        city: event.payment.shippingContact?.locality,
        state: event.payment.shippingContact?.administrativeArea?.toUpperCase(),
        phone: '',
        postal_code: event.payment.shippingContact?.postalCode,
        country_code: event.payment.shippingContact?.countryCode?.toUpperCase(),
        address_line1: event.payment.shippingContact?.addressLines?.[0],
        address_line2: event.payment.shippingContact?.addressLines?.[1],
        first_name:
          event.payment.shippingContact?.givenName ||
          firstName ||
          event.payment.shippingContact?.emailAddress?.split('@')?.[0],
        last_name: event.payment.shippingContact?.familyName,
        email: event.payment.shippingContact?.emailAddress,
        default: false
      };

      const paymentDetails = {
        card_type: event.payment.token.paymentMethod.network,
        last_four: event.payment.token.paymentMethod.displayName.replace(/[^\d.]/g, ''),
        source: PaymentSource.Apple
      };

      const billingAddress = {
        billing_address: {
          city: event.payment.billingContact?.locality,
          state: event.payment.billingContact?.administrativeArea?.toUpperCase(),
          postal_code: event.payment.billingContact?.postalCode,
          country_code: event.payment.billingContact?.countryCode?.toUpperCase(),
          address_line1: event.payment.billingContact?.addressLines?.[0],
          address_line2: event.payment.billingContact?.addressLines?.[1],
          first_name: event.payment.billingContact?.givenName,
          last_name: event.payment.billingContact?.familyName
        }
      };

      const applePaymentSummary: ApplePaymentSummary = {
        __typename: 'RestCartApplePaymentSummary',
        data: {
          __typename: 'RestCartApplePaymentSummaryData',
          currency: request.currencyCode,
          total: {
            __typename: 'RestApplePaymentSummaryTotal',
            ...request.total
          },
          shippingContact: {
            ...request.shippingContact,
            __typename: 'RestApplePaymentSummaryShippingContact'
          },
          lineItems:
            request.lineItems?.map(item => ({
              ...item,
              __typename: 'RestApplePaymentSummaryLineItem'
            })) || [],
          shippingMethods:
            request.shippingMethods?.map(item => ({
              ...item,
              __typename: 'RestApplePaymentSummaryShippingMethod'
            })) || [],
          status: {
            __typename: 'RestApplePaymentSummaryStatus',
            code: status || ''
          }
        }
      };

      const applePayCart = {
        is_guest: !isLoggedIn,
        country_code: request.countryCode,
        cart_id: cartId || '',
        address: shippingAddress,
        payment_source: PaymentSource.Apple,
        payment_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        other_payment_details: paymentDetails,
        payment_summary: billingAddress,
        ...(applePaymentSummary?.data.shippingMethods &&
          applePaymentSummary.data.shippingMethods[0] && {
            shipment_method: applePaymentSummary.data.shippingMethods[0].identifier
          })
      };

      try {
        const { data } = await placeOrder(applePayCart);

        const orderData = data?.checkout.data[0].attributes;

        if (orderData && orderData.guestAccessToken) {
          setUserData({
            ...NULL_USER,
            email: orderData.userEmail || event.payment.shippingContact?.emailAddress || '',
            accessToken: orderData.guestAccessToken,
            isMember: orderData.userIsMember || false,
            remember: true
          });
        }

        session.completePayment(ApplePaySession.STATUS_SUCCESS);

        orderData &&
          history.push(
            hrefFor.OrderConfirmationPage({
              orderNumber: orderData.orderNumber
            })
          );
      } catch (error) {
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    };
  }, [cartId, firstName, history, isLoggedIn, placeOrder, request, session, setUserData, status]);

  return null;
};

const Session: React.FC<SessionProps> = ({ session, cartId, request }) => {
  useEffect(() => {
    session.begin();
  }, [session]);

  return (
    <>
      <ApplePayOnValidateMerchant session={session} />

      <SessionPaymentAuthorized session={session} cartId={cartId} request={request} />
    </>
  );
};

interface Props {
  session?: ApplePaySession;
  cartId?: string;
  request?: ApplePayJS.ApplePayPaymentRequest;
}

export const ApplePayQuickBuySession: React.FC<Props> = ({ session, cartId, request }) => {
  if (!session) return null;

  if (!cartId) return null;

  if (!request) return null;

  return <Session session={session} cartId={cartId} request={request} />;
};
